import { followUpThresholdMs } from "./followUp";
import {
  routeReadingAt,
  type CallList,
  type LeadRecord,
  type LeadRoute,
  type OccasionCalendar,
} from "./leadQuality";
import type { NurtureHoldReason } from "./nurtureHold";
import { shopCalendarDay } from "./occasionPlanner";
import type { Situation } from "./situation";
import { TERMINAL_OUTCOMES } from "./types";
import { DAY_MS } from "./window";

/**
 * V2 SHEPHERD (Agent Brains 5.5 to 5.8, B3, section 7). The daily sweep's
 * planner: which planned touches and which silent leads become proposals, on
 * which route, and which are dropped with a reason.
 *
 * PURE. The occasion calendar, the call list, the records and the owner's quiet
 * dates go in; proposals and logged restraints come out. No clock, no IO. It
 * writes no wording and names no price: a proposal is a play CODE, a route and
 * a window. The routes are the ones `routeReadingAt` already reads, so the
 * window gate (T3) is never re-implemented here: a closed window routes to the
 * owner's call or to waiting, never to a draft request (F-02).
 *
 * Silence is a first-class action (B3): every touch dropped here comes back in
 * `suppressed` with its reason, so the caller logs a row for it.
 */

/**
 * How far ahead of its window a touch is proposed. The design's "a touch whose
 * window opens within 7 days". AWAITING RATIFICATION (Kabir): a conservative
 * reading, long enough for the owner to see a call-list item before the date
 * and short enough that the route is still a fair reading of the window.
 */
export const PROPOSAL_LEAD_MS = 7 * DAY_MS;

/** How long a stale-lead proposal's window runs from the day it is made. AWAITING RATIFICATION (Kabir). */
export const STALE_WINDOW_MS = 7 * DAY_MS;

/** The play code of a proposal made for a lead gone quiet, as opposed to an occasion. */
export const STALE_PLAY = "quiet_lead";

/** Most days one quiet-date overlap check walks; a touch window is a few days, this is a backstop. */
const MAX_OVERLAP_DAYS = 400;

/** An owner-entered bad date for one contact. Dates are `YYYY-MM-DD`, the month-day `MM-DD` (recurs yearly). */
export interface QuietDate {
  readonly contactId: string;
  readonly monthDay: string | null;
  readonly dateFrom: string | null;
  readonly dateThrough: string | null;
  readonly reason: string;
}

export type ProposalKind = "occasion" | "stale";

export interface PlannedProposal {
  readonly touchId: string;
  readonly kind: ProposalKind;
  readonly play: string;
  readonly contactId: string;
  readonly threadId: string;
  readonly route: LeadRoute;
  readonly windowFrom: Date;
  readonly windowThrough: Date;
  /** Citation ids only (section 10): the record rows behind the touch. */
  readonly cites: readonly string[];
}

/** Every reason a touch is dropped with a logged restraint. The event catalog's enum is this list. */
export const DROPPED_TOUCH_REASONS = [
  "quiet_date",
  "suppressed_contact",
  "occasion_touch_open",
  "customer_waiting",
  "no_route",
  // TA-1361 (CLAUDE.md section 3, B13): the thread is held for a person (nurtureHold.ts).
  "open_complaint",
  "class_d_thread",
  // TA-1371 C4: a person on the shop's team took the contact (built switched off, nurtureHold.ts).
  "human_takeover",
] as const;
export type DroppedTouchReason = (typeof DROPPED_TOUCH_REASONS)[number];

export interface DroppedTouch {
  readonly touchId: string;
  readonly contactId: string;
  readonly reason: DroppedTouchReason;
}

export interface ShepherdPlan {
  readonly proposals: readonly PlannedProposal[];
  readonly suppressed: readonly DroppedTouch[];
  /** Silent-lead proposals in `proposals`. */
  readonly stale: number;
  /**
   * Occasion touches whose contact token matches no record, so no contact id
   * exists to log a restraint against (B3: counted, never skipped silently).
   */
  readonly unattributed: number;
}

/**
 * Section 7: the customer is waiting on the SHOP, so a scheduled touch would
 * talk over an answer or a promise the shop owes. An open escalation, or a
 * pending answer_escalation or keep_promise action. A pending approve_draft is
 * its own deferral (a draft already waits), not this.
 */
export function customerWaitingOnShop(sit: Pick<Situation, "openEscalations" | "pendingActions">): boolean {
  return (
    sit.openEscalations.length > 0 ||
    sit.pendingActions.some((a) => a.kind === "answer_escalation" || a.kind === "keep_promise")
  );
}

export interface ShepherdPlanInput {
  readonly records: readonly LeadRecord[];
  readonly calendar: OccasionCalendar;
  readonly callList: CallList;
  readonly quietDates: readonly QuietDate[];
  readonly now: Date;
  /**
   * Threads whose situation says the customer is waiting on the shop
   * (`customerWaitingOnShop`), or whose shop owes an answer (the shared owed
   * read, packages/db/src/owedAnswer.ts), loaded by the caller. An occasion
   * touch or a stale touch on one is dropped with a logged reason (G4: a
   * holding line is a promise, never a quiet lead). A record's own unanswered
   * customer message counts too.
   */
  readonly waitingThreads?: ReadonlySet<string>;
  /**
   * TA-1361 (CLAUDE.md section 3, B13): threads held for a person, by thread
   * id, with the hold's reason (`nurtureHoldFor`, nurtureHold.ts: a class D
   * latest route, or a contact holding an open complaint), loaded by the
   * caller. An occasion touch or a stale touch on one is dropped with the
   * hold's reason as a logged restraint.
   */
  readonly nurtureHolds?: ReadonlyMap<string, NurtureHoldReason>;
  /**
   * G19 (AB 5.4 L4): the shop's IANA zone (`clients.timezone`). The stale
   * path's window [now, now + STALE_WINDOW_MS] is read as the shop's calendar
   * days against the quiet dates. Absent or null keeps UTC days; the caller
   * says so to the operator. An unknown zone throws `ShopTimeZoneUnknown`.
   */
  readonly shopTimeZone?: string | null;
  /** The route a contact's threads read at an instant. Defaults to `routeReadingAt`; a seam for tests. */
  readonly routeAt?: (records: readonly LeadRecord[], at: Date) => LeadRoute | null;
}

const isoDay = (d: Date): string => d.toISOString().slice(0, 10);
const monthDayOf = (d: Date): string => d.toISOString().slice(5, 10);

/**
 * Whether a quiet date touches any day of [from, through]. A month-day recurs
 * every year, so each day of the window is compared by its MM-DD; a range is
 * compared by date string, an absent end meaning that one day.
 *
 * G19: with `shopTimeZone` the two instants are read as the shop's calendar
 * days (`shopCalendarDay`); absent or null, as UTC days. A window already made
 * of calendar days (an occasion touch's) is passed without a zone, so it is
 * never shifted a second time.
 */
export function quietDateOverlaps(q: QuietDate, from: Date, through: Date, shopTimeZone?: string | null): boolean {
  const start = shopCalendarDay(from, shopTimeZone).getTime();
  const end = shopCalendarDay(through, shopTimeZone).getTime();
  for (let t = start, n = 0; t <= end && n < MAX_OVERLAP_DAYS; t += DAY_MS, n += 1) {
    const d = new Date(t);
    if (q.monthDay !== null && monthDayOf(d) === q.monthDay) return true;
    if (q.dateFrom !== null) {
      const day = isoDay(d);
      if (day >= q.dateFrom && day <= (q.dateThrough ?? q.dateFrom)) return true;
    }
  }
  return false;
}

const TERMINAL: readonly string[] = TERMINAL_OUTCOMES;

/**
 * Whether a record is a stale-path candidate: open (on the call list), the shop
 * answered last, quiet past the silence line, no terminal outcome. One reading
 * for the planner and for the caller that loads these threads' waiting state.
 */
function isStaleCandidate(r: LeadRecord, onList: ReadonlySet<string>, now: Date): boolean {
  if (!onList.has(r.threadId)) return false;
  if (r.lastCustomerMessageAt === null || r.firstUnanswered !== null) return false;
  if (r.outcomes.some((o) => TERMINAL.includes(o.label))) return false;
  return now.getTime() - r.lastCustomerMessageAt.getTime() >= followUpThresholdMs(null);
}

/**
 * G4 (AB 10.1): the threads the stale path reads, sorted, so the sweep can load
 * each one's situation and owed answer into `waitingThreads` before planning.
 */
export function staleCandidateThreads(input: Pick<ShepherdPlanInput, "records" | "callList" | "now">): string[] {
  const onList = new Set(input.callList.rows.map((row) => row.threadId));
  return [...new Set(input.records.filter((r) => isStaleCandidate(r, onList, input.now)).map((r) => r.threadId))].sort((a, b) =>
    a.localeCompare(b),
  );
}

export function planShepherdProposals(input: ShepherdPlanInput): ShepherdPlan {
  const { now } = input;
  const readRoute =
    input.routeAt ??
    ((records: readonly LeadRecord[], at: Date): LeadRoute | null => routeReadingAt(records, at)?.route.route ?? null);

  const byToken = new Map<string, string>();
  const byContact = new Map<string, LeadRecord[]>();
  for (const r of input.records) {
    byToken.set(r.contactPseudonym, r.contactId);
    byContact.set(r.contactId, [...(byContact.get(r.contactId) ?? []), r]);
  }
  const rankOf = new Map<string, number>();
  for (const row of input.callList.rows) rankOf.set(row.threadId, row.rank);

  // An unknown zone is refused before anything is planned, whether or not a stale lead reads it.
  shopCalendarDay(now, input.shopTimeZone);
  const quietFor = (contactId: string): QuietDate[] => input.quietDates.filter((q) => q.contactId === contactId);
  /** `zone` only for a window of instants; an occasion touch's days are calendar days already. */
  const quietHit = (contactId: string, from: Date, through: Date, zone?: string | null): boolean =>
    quietFor(contactId).some((q) => quietDateOverlaps(q, from, through, zone));

  const proposals: PlannedProposal[] = [];
  const suppressed: DroppedTouch[] = [];
  const horizon = now.getTime() + PROPOSAL_LEAD_MS;

  // 1. Occasion touches whose window opens inside the lead time.
  let unattributed = 0;
  const unanswered = new Set(input.records.filter((r) => r.firstUnanswered !== null).map((r) => r.threadId));
  for (const row of input.calendar.rows) {
    if (row.touch.from.getTime() > horizon) continue;
    const contactId = byToken.get(row.contactPseudonym);
    if (contactId === undefined) {
      // No contact id exists to log a restraint against: counted, and the caller reports the count.
      unattributed += 1;
      continue;
    }
    // TA-1361: a held thread is a person's, named first so the row says what would lift it.
    const held = input.nurtureHolds?.get(row.routeThreadId);
    if (held !== undefined) {
      suppressed.push({ touchId: row.touch.id, contactId, reason: held });
      continue;
    }
    if (quietHit(contactId, row.touch.from, row.touch.through)) {
      suppressed.push({ touchId: row.touch.id, contactId, reason: "quiet_date" });
      continue;
    }
    if (input.waitingThreads?.has(row.routeThreadId) === true || unanswered.has(row.routeThreadId)) {
      suppressed.push({ touchId: row.touch.id, contactId, reason: "customer_waiting" });
      continue;
    }
    proposals.push({
      touchId: row.touch.id,
      kind: "occasion",
      play: row.touch.play,
      contactId,
      threadId: row.routeThreadId,
      route: row.route.route,
      windowFrom: row.touch.from,
      windowThrough: row.touch.through,
      cites: row.touch.cites.map((c) => c.id),
    });
  }

  // 2. Silent leads: open (on the call list), the shop answered last, quiet past
  // the silence line, no terminal outcome. Routed at now, so on Meta this is
  // almost always the owner's call (the 24h window is long closed).
  const occasionContacts = new Set(proposals.map((p) => p.contactId));
  let stale = 0;
  const onList = new Set(rankOf.keys());
  for (const r of [...input.records].sort((a, b) => a.threadId.localeCompare(b.threadId))) {
    if (!isStaleCandidate(r, onList, now) || r.lastCustomerMessageAt === null) continue;

    const touchId = `stale:${r.threadId}:${isoDay(r.lastCustomerMessageAt)}`;
    if (r.contact.suppressed === true) {
      suppressed.push({ touchId, contactId: r.contactId, reason: "suppressed_contact" });
      continue;
    }
    // TA-1361 (CLAUDE.md section 3, B13): a class D thread or an open complaint is a person's.
    const held = input.nurtureHolds?.get(r.threadId);
    if (held !== undefined) {
      suppressed.push({ touchId, contactId: r.contactId, reason: held });
      continue;
    }
    // G4 (AB 10.1): the shop owes this customer an answer or a promise (a holding
    // line, an open escalation): a nurture nudge would talk over it.
    if (input.waitingThreads?.has(r.threadId) === true) {
      suppressed.push({ touchId, contactId: r.contactId, reason: "customer_waiting" });
      continue;
    }
    if (occasionContacts.has(r.contactId)) {
      suppressed.push({ touchId, contactId: r.contactId, reason: "occasion_touch_open" });
      continue;
    }
    const through = new Date(now.getTime() + STALE_WINDOW_MS);
    if (quietHit(r.contactId, now, through, input.shopTimeZone)) {
      suppressed.push({ touchId, contactId: r.contactId, reason: "quiet_date" });
      continue;
    }
    const route = readRoute(byContact.get(r.contactId) ?? [r], now);
    if (route === null) {
      suppressed.push({ touchId, contactId: r.contactId, reason: "no_route" });
      continue;
    }
    proposals.push({
      touchId,
      kind: "stale",
      play: STALE_PLAY,
      contactId: r.contactId,
      threadId: r.threadId,
      route,
      windowFrom: now,
      windowThrough: through,
      cites: [r.threadId],
    });
    stale += 1;
  }

  // Order is expected value, never recency: the call list's rank, unranked last, ties by touch id.
  const rank = (p: PlannedProposal): number => rankOf.get(p.threadId) ?? Number.MAX_SAFE_INTEGER;
  proposals.sort((a, b) => rank(a) - rank(b) || a.touchId.localeCompare(b.touchId));
  return { proposals, suppressed, stale, unattributed };
}
